import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';

import ConfirmModal from '../../../../../Components/crude/ConfirmModal';
import ActionButton from '../../../../../Components/Common/ActionButton';
import * as api from '../planningApi';
import { DOC_SOURCE } from '../constants';
import { formatDateTime } from '../../formatters';

// Bytes → label ringkas untuk baris file di popup Master Document.
const formatFileSize = bytes => {
  if (!bytes || bytes <= 0) return '';
  return bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

// Supporting Document tab: documents come either from Master Document Upload
// (checklist, section 13) or a custom multipart upload (section 14). Every
// action is a live call, after which we pull the fresh document list.
const SupportingDocumentTab = ({
  planningId,
  detail,
  setDetail,
  refresh,
  readOnly = false,
}) => {
  const docs = detail.supportingDocuments;
  const fileInputRef = useRef(null);

  const [busy, setBusy] = useState(false);

  const [masterModal, setMasterModal] = useState(false);
  const [masterRows, setMasterRows] = useState([]);
  const [masterSearch, setMasterSearch] = useState('');
  const [masterLoading, setMasterLoading] = useState(false);
  const [checkedMaster, setCheckedMaster] = useState({});

  const [customModal, setCustomModal] = useState(false);
  const [customFile, setCustomFile] = useState(null);
  const [customName, setCustomName] = useState('');
  const [customShowToDmut, setCustomShowToDmut] = useState(true);
  const [dragOver, setDragOver] = useState(false);

  const [deleteId, setDeleteId] = useState(null);

  const reloadDocs = useCallback(async () => {
    const fresh = await refresh();
    setDetail(prev => ({
      ...prev,
      supportingDocuments: fresh.supportingDocuments,
    }));
  }, [refresh, setDetail]);

  // ---- Master Document Upload modal (live, top-5, excludes already-added) ---
  const loadMaster = useCallback(
    search => {
      setMasterLoading(true);
      api
        .lookupDocumentFromMaster(planningId, search)
        .then(setMasterRows)
        .catch(error =>
          toast.error(`Failed to load master documents: ${error}`)
        )
        .finally(() => setMasterLoading(false));
    },
    [planningId]
  );

  const openMasterModal = () => {
    setCheckedMaster({});
    setMasterSearch('');
    setMasterModal(true);
    loadMaster('');
  };

  // Debounce the master search box.
  useEffect(() => {
    if (!masterModal) return undefined;
    const t = setTimeout(() => loadMaster(masterSearch.trim()), 350);
    return () => clearTimeout(t);
  }, [masterSearch, masterModal, loadMaster]);

  const toggleMaster = id =>
    setCheckedMaster(prev => ({ ...prev, [id]: !prev[id] }));

  const addFromMaster = async () => {
    const selected = masterRows.filter(m => checkedMaster[m.id]);
    if (selected.length === 0) {
      toast.error('Select at least one document');
      return;
    }
    setBusy(true);
    try {
      await api.addDocumentsFromMaster(
        planningId,
        selected.map(m => ({ crudeDocumentUploadId: m.id, showToDmut: true }))
      );
      await reloadDocs();
      toast.success(`${selected.length} document(s) added`);
      setMasterModal(false);
    } catch (error) {
      toast.error(`Failed to add documents: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  // ---- Upload Custom File modal -------------------------------------------
  const onBrowse = () => fileInputRef.current?.click();

  const beginCustom = file => {
    if (!file) return;
    setCustomFile(file);
    setCustomName(file.name.replace(/\.[^.]+$/, ''));
    setCustomShowToDmut(true);
    setCustomModal(true);
  };

  const onFilePicked = e => {
    const file = e.target.files?.[0];
    beginCustom(file);
    e.target.value = '';
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    beginCustom(e.dataTransfer.files?.[0]);
  };

  const addCustom = async () => {
    if (!customName.trim()) {
      toast.error('Document Name is required');
      return;
    }
    if (!customFile) {
      toast.error('A file is required');
      return;
    }
    setBusy(true);
    try {
      await api.uploadDocument(planningId, {
        documentName: customName.trim(),
        showToDmut: customShowToDmut,
        file: customFile,
      });
      await reloadDocs();
      toast.success('Document uploaded');
      setCustomModal(false);
      setCustomFile(null);
      setCustomName('');
    } catch (error) {
      toast.error(`Upload failed: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  const toggleShowToDmut = async doc => {
    const next = !doc.showToDmut;
    // Optimistic — reflect immediately, roll back on failure.
    setDetail(prev => ({
      ...prev,
      supportingDocuments: prev.supportingDocuments.map(d =>
        d.id === doc.id ? { ...d, showToDmut: next } : d
      ),
    }));
    try {
      await api.setDocumentShowToDmut(planningId, doc.id, next);
    } catch (error) {
      toast.error(`Failed to update Show to Registered Supplier: ${error}`);
      setDetail(prev => ({
        ...prev,
        supportingDocuments: prev.supportingDocuments.map(d =>
          d.id === doc.id ? { ...d, showToDmut: doc.showToDmut } : d
        ),
      }));
    }
  };

  const confirmDelete = async () => {
    const id = deleteId;
    setDeleteId(null);
    setBusy(true);
    try {
      await api.deleteDocument(planningId, id);
      await reloadDocs();
      toast.success('Document removed');
    } catch (error) {
      toast.error(`Failed to remove document: ${error}`);
    } finally {
      setBusy(false);
    }
  };

  const handleView = async doc => {
    try {
      const blob = await api.downloadDocument(planningId, doc.id);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      // Revoke shortly after so the new tab has time to load it.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error(`Failed to open document: ${error}`);
    }
  };

  return (
    <React.Fragment>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <div>
          <h5 className='fw-semibold mb-1'>
            <i className='ri-file-list-3-line me-2'></i>
            Document Information
          </h5>
          <p className='text-muted mb-0 fs-12'>
            Manage supporting documents for this planning
          </p>
        </div>
      </div>

      <Card className='border shadow-sm'>
        <CardBody>
          <Row>
            {/* Left: upload action cards */}
            {!readOnly && (
              <Col sm={3}>
                <h6
                  className='text-uppercase fw-semibold mb-3 fs-12'
                  style={{ letterSpacing: '0.5px' }}
                >
                  Upload Document
                </h6>
                <div
                  className='sd-upload-card mb-3'
                  role='button'
                  onClick={openMasterModal}
                >
                  <div className='sd-upload-card-icon master'>
                    <i className='ri-database-2-line'></i>
                  </div>
                  <div className='sd-upload-card-text'>
                    <h6>Master Document</h6>
                    <p>Select from master database</p>
                  </div>
                </div>
                <div
                  className={`sd-upload-card${dragOver ? ' drag-over' : ''}`}
                  role='button'
                  onClick={onBrowse}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className='sd-upload-card-icon custom'>
                    <i className='ri-upload-cloud-2-line'></i>
                  </div>
                  <div className='sd-upload-card-text'>
                    <h6>Upload Custom File</h6>
                    <p>Browse from your device</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type='file'
                    hidden
                    accept='.pdf,.doc,.docx,.xls,.xlsx'
                    onChange={onFilePicked}
                  />
                </div>
              </Col>
            )}

            {/* Right: document list */}
            <Col sm={readOnly ? 12 : 9}>
              <h6
                className='text-uppercase fw-semibold mb-3 fs-12'
                style={{ letterSpacing: '0.5px' }}
              >
                Selected / Uploaded Document
              </h6>
              {docs.length === 0 ? (
                <div className='sd-empty'>
                  <i className='ri-file-text-line'></i>
                  <p>No documents uploaded yet</p>
                </div>
              ) : (
                <>
                  <div className='sd-doc-header'>
                    <span style={{ flex: '2 1 0' }}>Document Name</span>
                    <span style={{ flex: '1 1 0' }}>Source</span>
                    <span style={{ flex: '1 1 0' }}>Last Updated</span>
                    <span style={{ flex: '1 1 0', textAlign: 'center' }}>
                      Show to Registered Supplier?
                    </span>
                    <span style={{ flex: '1.4 1 0', textAlign: 'right' }}>
                      Action
                    </span>
                  </div>
                  {docs.map(d => (
                    <div key={d.id} className='sd-doc-row'>
                      <div style={{ flex: '2 1 0' }}>
                        <span className='fw-semibold fs-13'>{d.name}</span>
                      </div>
                      <div style={{ flex: '1 1 0' }}>
                        <span
                          className={`sd-badge ${
                            d.source === DOC_SOURCE.MASTER ? 'master' : 'custom'
                          }`}
                        >
                          {d.source === DOC_SOURCE.MASTER
                            ? 'Master Document'
                            : 'Upload Custom'}
                        </span>
                      </div>
                      <div style={{ flex: '1 1 0' }}>
                        <span className='text-muted fs-12'>
                          {formatDateTime(d.lastUpdated)}
                        </span>
                      </div>
                      <div
                        className='form-check form-switch form-switch-secondary d-flex justify-content-center mb-0'
                        style={{ flex: '1 1 0' }}
                      >
                        <Input
                          className='form-check-input'
                          type='checkbox'
                          role='switch'
                          checked={!!d.showToDmut}
                          disabled={readOnly}
                          onChange={() => toggleShowToDmut(d)}
                        />
                      </div>
                      <div style={{ flex: '1.4 1 0', textAlign: 'right' }}>
                        <div className='d-flex gap-1 justify-content-end'>
                          <ActionButton
                            variant='tertiary'
                            size='sm'
                            icon='ri-eye-line'
                            onClick={() => handleView(d)}
                          >
                            View
                          </ActionButton>
                          {!readOnly && (
                            <ActionButton
                              variant='destructive'
                              size='sm'
                              icon='ri-delete-bin-line'
                              confirm={false}
                              disabled={busy}
                              onClick={() => setDeleteId(d.id)}
                            >
                              Delete
                            </ActionButton>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Master document picker modal */}
      <Modal isOpen={masterModal} toggle={() => setMasterModal(false)} centered>
        <ModalHeader toggle={() => setMasterModal(false)}>
          Master Document Upload
        </ModalHeader>
        <ModalBody>
          <Input
            type='text'
            className='mb-3'
            placeholder='Search by document / file name...'
            value={masterSearch}
            onChange={e => setMasterSearch(e.target.value)}
          />
          <Table className='table-sm align-middle mb-0'>
            <thead className='table-light'>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Document Name</th>
              </tr>
            </thead>
            <tbody>
              {masterLoading ? (
                <tr>
                  <td colSpan={2} className='text-center text-muted py-3'>
                    <Spinner size='sm' className='me-2' />
                    Loading…
                  </td>
                </tr>
              ) : masterRows.length === 0 ? (
                <tr>
                  <td colSpan={2} className='text-center text-muted py-3'>
                    No documents found
                  </td>
                </tr>
              ) : (
                masterRows.map(m => (
                  <tr key={m.id}>
                    <td>
                      <Input
                        type='checkbox'
                        className='form-check-input'
                        checked={!!checkedMaster[m.id]}
                        onChange={() => toggleMaster(m.id)}
                      />
                    </td>
                    <td>
                      {/* Lookup kini mengirim info kaya (documentName, type,
                          description, file) — `m.text` dipertahankan sebagai
                          fallback bentuk lama {id, text}. */}
                      <span className='fw-semibold d-block'>
                        {m.documentName || m.text}
                      </span>
                      <div className='d-flex flex-wrap align-items-center gap-2 mt-1'>
                        {m.documentType && (
                          <span className='badge bg-secondary-subtle text-secondary'>
                            {m.documentType}
                          </span>
                        )}
                        {m.description && (
                          <small className='text-muted'>{m.description}</small>
                        )}
                      </div>
                      {m.fileName && (
                        <small className='text-primary d-block mt-1'>
                          <i className='ri-file-text-line me-2'></i>
                          {m.fileName}
                          {m.fileSize ? ` (${formatFileSize(m.fileSize)})` : ''}
                        </small>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </ModalBody>
        <ModalFooter>
          <ActionButton
            variant='secondary'
            size='sm'
            icon='ri-close-line'
            onClick={() => setMasterModal(false)}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant='primary'
            size='sm'
            icon='ri-add-line'
            onClick={addFromMaster}
            disabled={busy}
          >
            Add Document
          </ActionButton>
        </ModalFooter>
      </Modal>

      {/* Custom file details modal */}
      <Modal isOpen={customModal} toggle={() => setCustomModal(false)} centered>
        <ModalHeader toggle={() => setCustomModal(false)}>
          Upload Custom File
        </ModalHeader>
        <ModalBody>
          <div className='mb-3'>
            <Label className='form-label'>Selected File</Label>
            <Input
              type='text'
              value={customFile?.name || ''}
              readOnly
              disabled
            />
          </div>
          <div className='mb-3'>
            <Label className='form-label'>
              Document Name <span className='text-danger'>*</span>
            </Label>
            <Input
              type='text'
              maxLength={250}
              value={customName}
              onChange={e => setCustomName(e.target.value)}
            />
          </div>
          <div className='form-check form-switch mb-1'>
            <Input
              type='checkbox'
              role='switch'
              className='form-check-input'
              id='custom-show-dmut'
              checked={customShowToDmut}
              onChange={e => setCustomShowToDmut(e.target.checked)}
            />
            <Label for='custom-show-dmut' className='form-check-label'>
              Show to Registered Supplier
            </Label>
          </div>
        </ModalBody>
        <ModalFooter>
          <ActionButton
            variant='secondary'
            size='sm'
            icon='ri-close-line'
            onClick={() => setCustomModal(false)}
          >
            Cancel
          </ActionButton>
          <ActionButton
            variant='primary'
            size='sm'
            icon='ri-add-line'
            onClick={addCustom}
            disabled={busy}
          >
            Add Document
          </ActionButton>
        </ModalFooter>
      </Modal>

      <ConfirmModal
        show={deleteId !== null}
        title='Delete Document'
        message='Are you sure you want to remove this document from the list?'
        confirmLabel='Delete'
        confirmColor='danger'
        onConfirm={confirmDelete}
        onClose={() => setDeleteId(null)}
      />
    </React.Fragment>
  );
};

export default SupportingDocumentTab;
